import { useSeoMeta } from '@unhead/react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Cell,
} from 'recharts';
import { Header } from '@/components/signature/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useAnalytics } from '@/hooks/useAnalytics';
import { isAdminPubkey } from '@/lib/analyticsConfig';
import { LoginArea } from '@/components/auth/LoginArea';
import { BCH_GREEN } from '@/lib/signature';
import {
  FileSignature,
  Copy,
  Users,
  MousePointerClick,
  TrendingUp,
  ArrowLeft,
  ShieldAlert,
  RefreshCw,
  Lock,
} from 'lucide-react';

const BAR_COLORS = [
  '#0AC18E',
  '#0891b2',
  '#6366f1',
  '#f59e0b',
  '#ef4444',
  '#8b5cf6',
  '#10b981',
  '#ec4899',
  '#64748b',
];

function fmtDay(d: string) {
  const date = new Date(`${d}T00:00:00Z`);
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

function StatCard({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  sub?: string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-5">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
          {icon}
        </div>
        <div>
          <div className="text-2xl font-bold leading-none">{value}</div>
          <div className="mt-1 text-xs text-muted-foreground">{label}</div>
          {sub && <div className="text-[11px] text-primary">{sub}</div>}
        </div>
      </CardContent>
    </Card>
  );
}

const Admin = () => {
  useSeoMeta({
    title: 'Analytics · BCH Signature',
    description: 'Admin analytics dashboard for BCH Signature.',
  });

  const { user } = useCurrentUser();
  const isAdmin = isAdminPubkey(user?.pubkey);
  const { data, isLoading, error, refetch, isFetching } = useAnalytics();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container px-4 py-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <Link
              to="/"
              className="mb-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> Back to builder
            </Link>
            <h1 className="flex items-center gap-2 text-2xl font-bold">
              <TrendingUp className="h-6 w-6 text-primary" /> Analytics
            </h1>
            <p className="text-sm text-muted-foreground">
              Aggregate, privacy-preserving usage over the last 30 days.
            </p>
          </div>
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="gap-2"
            >
              <RefreshCw
                className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>
          )}
        </div>

        {/* Not logged in */}
        {!user && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-4 py-12 text-center">
              <Lock className="h-8 w-8 text-muted-foreground" />
              <p className="max-w-sm text-muted-foreground">
                This dashboard is restricted. Log in with the admin Nostr account
                to view analytics.
              </p>
              <LoginArea className="max-w-60" />
            </CardContent>
          </Card>
        )}

        {/* Logged in but not admin */}
        {user && !isAdmin && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <ShieldAlert className="h-8 w-8 text-destructive" />
              <p className="max-w-sm text-muted-foreground">
                Your account isn&apos;t authorized to view this dashboard.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Admin, loading */}
        {isAdmin && isLoading && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-xl" />
              ))}
            </div>
            <Skeleton className="h-72 rounded-xl" />
            <div className="grid gap-6 lg:grid-cols-2">
              <Skeleton className="h-72 rounded-xl" />
              <Skeleton className="h-72 rounded-xl" />
            </div>
          </div>
        )}

        {/* Admin, error */}
        {isAdmin && error && !isLoading && (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
              <ShieldAlert className="h-8 w-8 text-destructive" />
              <p className="max-w-md text-muted-foreground">
                {(error as Error).message}
              </p>
              <p className="max-w-md text-xs text-muted-foreground">
                If the site isn&apos;t deployed to Netlify yet, the analytics
                functions won&apos;t respond. Deploy the project, then refresh.
              </p>
              <Button variant="outline" size="sm" onClick={() => refetch()}>
                Try again
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Admin, data */}
        {isAdmin && data && !isLoading && (
          <div className="space-y-6">
            {/* KPI cards */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard
                icon={<FileSignature className="h-5 w-5" />}
                label="Signatures created (30d)"
                value={data.totals.created.toLocaleString()}
                sub={`${data.totals.createdWeek} this week`}
              />
              <StatCard
                icon={<Copy className="h-5 w-5" />}
                label="Signatures copied (30d)"
                value={data.totals.copied.toLocaleString()}
                sub={`${data.totals.copiedWeek} this week`}
              />
              <StatCard
                icon={<Users className="h-5 w-5" />}
                label="Active users today"
                value={data.totals.activeToday.toLocaleString()}
                sub={`${data.totals.activeWeek} active (7d)`}
              />
              <StatCard
                icon={<MousePointerClick className="h-5 w-5" />}
                label="Link clicks (30d)"
                value={data.totals.clicks.toLocaleString()}
                sub={`${data.totals.clicksWeek} this week`}
              />
            </div>

            {/* Growth summary */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <GrowthPill label="Today" value={data.totals.createdDay} />
              <GrowthPill label="This week" value={data.totals.createdWeek} />
              <GrowthPill label="This month" value={data.totals.createdMonth} />
            </div>

            {/* Trend chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Daily activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-72 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={data.series}
                      margin={{ top: 5, right: 10, left: -18, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="gCreated" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={BCH_GREEN} stopOpacity={0.35} />
                          <stop offset="95%" stopColor={BCH_GREEN} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gCopied" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="gClicks" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" opacity={0.15} vertical={false} />
                      <XAxis
                        dataKey="day"
                        tickFormatter={fmtDay}
                        tick={{ fontSize: 11 }}
                        minTickGap={24}
                      />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={40} />
                      <Tooltip
                        labelFormatter={(l) => fmtDay(String(l))}
                        contentStyle={{
                          borderRadius: 12,
                          border: '1px solid hsl(var(--border))',
                          fontSize: 12,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="created"
                        name="Created"
                        stroke={BCH_GREEN}
                        fill="url(#gCreated)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="copied"
                        name="Copied"
                        stroke="#6366f1"
                        fill="url(#gCopied)"
                        strokeWidth={2}
                      />
                      <Area
                        type="monotone"
                        dataKey="clicks"
                        name="Clicks"
                        stroke="#f59e0b"
                        fill="url(#gClicks)"
                        strokeWidth={2}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <Legend
                  items={[
                    { label: 'Created', color: BCH_GREEN },
                    { label: 'Copied', color: '#6366f1' },
                    { label: 'Clicks', color: '#f59e0b' },
                  ]}
                />
              </CardContent>
            </Card>

            <div className="grid gap-6 lg:grid-cols-2">
              {/* Template usage */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Template usage</CardTitle>
                </CardHeader>
                <CardContent>
                  <UsageBarChart
                    data={Object.entries(data.templates)
                      .map(([name, value]) => ({ name, value }))
                      .sort((a, b) => b.value - a.value)}
                  />
                </CardContent>
              </Card>

              {/* Link clicks by type */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Clicks by link type</CardTitle>
                </CardHeader>
                <CardContent>
                  <UsageBarChart
                    data={Object.entries(data.links)
                      .map(([name, value]) => ({ name, value }))
                      .sort((a, b) => b.value - a.value)}
                  />
                </CardContent>
              </Card>
            </div>

            <p className="pb-4 text-center text-xs text-muted-foreground">
              Privacy-first: only anonymous, aggregate counts are stored. No email
              content, recipients, IP addresses, or personal data is collected.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

function GrowthPill({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="flex items-center justify-between p-4">
        <span className="text-sm text-muted-foreground">{label}</span>
        <span className="flex items-center gap-2 text-lg font-bold">
          <Badge variant="secondary" className="font-mono">
            +{value.toLocaleString()}
          </Badge>
          <span className="text-xs font-normal text-muted-foreground">created</span>
        </span>
      </CardContent>
    </Card>
  );
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="mt-3 flex flex-wrap justify-center gap-4">
      {items.map((it) => (
        <span key={it.label} className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <span
            className="h-2.5 w-2.5 rounded-full"
            style={{ backgroundColor: it.color }}
          />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function UsageBarChart({ data }: { data: { name: string; value: number }[] }) {
  const total = data.reduce((a, b) => a + b.value, 0);
  if (total === 0) {
    return (
      <div className="flex h-56 items-center justify-center text-sm text-muted-foreground">
        No data yet.
      </div>
    );
  }
  return (
    <div className="h-56 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 0, right: 12, left: 8, bottom: 0 }}
        >
          <XAxis type="number" hide allowDecimals={false} />
          <YAxis
            type="category"
            dataKey="name"
            width={72}
            tick={{ fontSize: 12, textTransform: 'capitalize' }}
          />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))', opacity: 0.4 }}
            contentStyle={{ borderRadius: 12, border: '1px solid hsl(var(--border))', fontSize: 12 }}
          />
          <Bar dataKey="value" radius={[0, 6, 6, 0]}>
            {data.map((_, i) => (
              <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default Admin;
